import { isAnswered, selectedOptions } from './answers';
import type { GraphItem, GraphNode, TemplateGraph } from './graph';
import type { RuntimeNode, RuntimeTree } from './instances';
import { answerKeyFor } from './keys';
import type {
    Answer,
    AnswerKey,
    AnswerSet,
    NodeId,
    NodeItemId,
    NodeItemOptionId,
    PackageNodeItemOption,
    QuestionId,
    QuestionOptionId,
} from './types';

/**
 * VisibilityWalker — DFS in pre-order over de itemgraaf van één knoop.
 *
 * Drie regels maken de wandeling exact (`graph-semantics.md` §2):
 * 1. alle geselecteerde antwoordopties worden gevolgd, in randvolgorde;
 * 2. waardetypes volgen onvoorwaardelijk alle randen van hun item;
 * 3. bij de eerste onbeantwoorde vraag stopt de héle wandeling van die knoop —
 *    ook nog niet bezochte zustertakken worden niet meer verzameld.
 *
 * Daarbovenop komt één regel van onszelf: een `number`-vraag beantwoord met **0**
 * volgt geen enkele rand (`isZeroQuantity`). Nul lichtstraten, nul m² te
 * vervangen: dan is er niets om over door te vragen, niets te oordelen en niets
 * te betalen. De vraag zelf blijft zichtbaar en telt als beantwoord; wat eronder
 * hing vervalt, en de antwoorden daarop blijven bewaard voor als het aantal
 * weer omhoog gaat. Een multi-container doet hetzelfde met zijn aantalsvraag:
 * 0 instanties, dus geen gekloonde subboom (`instances.ts`).
 */

/** Een gevolgde rand; de basis voor oordeel, kosten en rapportteksten. */
export interface ChosenOption {
    readonly itemId: NodeItemId;
    readonly questionId: QuestionId;
    readonly questionKey: AnswerKey;
    readonly optionId: NodeItemOptionId;
    readonly questionOptionId: QuestionOptionId;
    readonly option: PackageNodeItemOption;
}

export interface NodeVisibility {
    readonly nodeId: NodeId;
    readonly parentId: NodeId | null;
    readonly childIds: readonly NodeId[];
    readonly name: string;
    readonly displayName: string;
    readonly sectionPath: string;
    readonly level: number;
    readonly reportSectionPath: string;
    readonly reportLevel: number;
    readonly inReport: boolean;
    readonly isMultiContainer: boolean;
    readonly isMultiInstance: boolean;
    readonly multiIndex: number | null;
    readonly multiCount: number | null;
    readonly multiCountKey: AnswerKey | null;
    readonly multiCountAnswered: boolean;
    /**
     * De zichtbare vragen van deze knoop, in bezoekvolgorde en uniek - ook een vraag
     * die al in een eerder onderdeel staat (zie `VisibilityResult.questionOwners`).
     */
    readonly questionKeys: readonly AnswerKey[];
    /** Dezelfde vragen als template-id, op dezelfde posities. */
    readonly questionIds: readonly QuestionId[];
    /**
     * Aantal vragen dat dit onderdeel zelf stelt, inclusief de aantalsvraag van een
     * multi-container. Een vraag die al in een eerder onderdeel staat, telt daar en
     * niet hier: zo tellen de onderdelen samen op tot de teller van het dossier.
     */
    readonly visibleCount: number;
    readonly answeredCount: number;
    /** Waar "Eerst onvolledig" naartoe springt. */
    readonly firstUnansweredQuestionKey: AnswerKey | null;
    readonly firstUnansweredQuestionId: QuestionId | null;
    /** Het eigen pad is compleet. */
    readonly allAnswered: boolean;
    /** Het eigen pad én alle afstammelingen zijn compleet. */
    readonly allAnsweredDeep: boolean;
    readonly chosen: readonly ChosenOption[];
}

export interface VisibilityResult {
    readonly nodes: ReadonlyMap<NodeId, NodeVisibility>;
    /** Alle knopen in pre-order. */
    readonly order: readonly NodeId[];
    /** De unie van alle zichtbare vraagsleutels — zichtbaarheid is bereikbaarheid. */
    readonly visibleQuestionKeys: ReadonlySet<AnswerKey>;
    /**
     * Per zichtbare vraag het onderdeel waar hij hoort: het eerste in boomvolgorde
     * waar hij verschijnt. Een vraag die in meer onderdelen staat ("Type woning" in 28)
     * heeft één antwoord; hij wordt daar gesteld en geteld, en elders alleen getoond.
     */
    readonly questionOwners: ReadonlyMap<AnswerKey, NodeId>;
    readonly answeredQuestionKeys: ReadonlySet<AnswerKey>;
    readonly firstIncompleteNodeId: NodeId | null;
}

export interface NodeTraversal {
    readonly questionKeys: AnswerKey[];
    readonly questionIds: QuestionId[];
    readonly chosen: ChosenOption[];
    readonly haltedKey: AnswerKey | null;
    readonly haltedQuestionId: QuestionId | null;
    /** Het item waarop de wandeling stopte; nodig om de sweep-optie te kiezen. */
    readonly haltedItemId: NodeItemId | null;
}

/** Wandelt één knoop af. Exporteert de ruwe uitkomst voor sweep en kosten. */
export function traverseNode(
    graph: TemplateGraph,
    answers: AnswerSet,
    graphNode: GraphNode,
    scope: string,
): NodeTraversal {
    const result: NodeTraversal = {
        questionKeys: [],
        questionIds: [],
        chosen: [],
        haltedKey: null,
        haltedQuestionId: null,
        haltedItemId: null,
    };

    if (!graphNode.startItem) {
        return result;
    }

    const state = { halted: false };
    const seenItems = new Set<NodeItemId>();
    const seenQuestions = new Set<AnswerKey>();

    const visit = (item: GraphItem): void => {
        if (state.halted || seenItems.has(item.id)) {
            return;
        }

        seenItems.add(item.id);

        const question = graph.questionsById.get(item.questionId);
        const key = answerKeyFor(item.questionId, scope, question?.question.ask_once === true);

        if (!seenQuestions.has(key)) {
            seenQuestions.add(key);
            result.questionKeys.push(key);
            result.questionIds.push(item.questionId);
        }

        const answer: Answer | undefined = answers[key];

        if (!question || !isAnswered(question, answer)) {
            state.halted = true;
            (result as { haltedKey: AnswerKey | null }).haltedKey = key;
            (result as { haltedQuestionId: QuestionId | null }).haltedQuestionId = item.questionId;
            (result as { haltedItemId: NodeItemId | null }).haltedItemId = item.id;

            return;
        }

        for (const option of selectedOptions(question, item, answer)) {
            if (state.halted) {
                return;
            }

            result.chosen.push({
                itemId: item.id,
                questionId: item.questionId,
                questionKey: key,
                optionId: option.id,
                questionOptionId: option.question_option_id,
                option,
            });

            const next =
                option.next_item_id === null
                    ? undefined
                    : graphNode.itemsById.get(option.next_item_id);

            if (next) {
                visit(next);
            }
        }
    };

    visit(graphNode.startItem);

    return result;
}

/**
 * Berekent de zichtbaarheid van het hele dossier: per knoop de zichtbare
 * vragen, het aantal beantwoord en de eerste openstaande vraag.
 */
export function walkVisibility(
    graph: TemplateGraph,
    answers: AnswerSet,
    tree: RuntimeTree,
): VisibilityResult {
    const nodes = new Map<NodeId, NodeVisibility>();
    const visibleQuestionKeys = new Set<AnswerKey>();
    const answeredQuestionKeys = new Set<AnswerKey>();
    const questionOwners = new Map<AnswerKey, NodeId>();

    for (const nodeId of tree.order) {
        const runtimeNode = tree.nodes.get(nodeId) as RuntimeNode;
        const traversal = traverseNode(graph, answers, runtimeNode.graphNode, runtimeNode.scope);
        const multiCountAnswered = runtimeNode.multiCountKey
            ? isMultiCountAnswered(answers, runtimeNode.multiCountKey)
            : true;
        let ownVisible = 0;
        let ownAnswered = 0;

        /*
         * Een aantalsvraag die nog leeg staat, staat op nul, en nul is hier een
         * antwoord: "er zijn er geen". De wandeling stopt er nog steeds — wat van
         * dat aantal afhangt hoort pas open te gaan als er een aantal staat, en
         * dat is precies hoe de vragenlijst zelf is gebouwd — maar de teller
         * markeert hem niet langer als onbeantwoord, en het onderdeel blijft er
         * niet eeuwig om openstaan.
         */
        const haltedOnCount =
            traversal.haltedQuestionId !== null &&
            graph.questionsById.get(traversal.haltedQuestionId)?.question.type === 'number';

        for (const key of traversal.questionKeys) {
            visibleQuestionKeys.add(key);

            if (key !== traversal.haltedKey || haltedOnCount) {
                answeredQuestionKeys.add(key);
            }

            /* Pre-order: het eerste onderdeel dat een vraag bereikt, stelt hem. */
            if (!questionOwners.has(key)) {
                questionOwners.set(key, nodeId);
                ownVisible += 1;
                ownAnswered += key === traversal.haltedKey && !haltedOnCount ? 0 : 1;
            }
        }

        if (runtimeNode.multiCountKey) {
            visibleQuestionKeys.add(runtimeNode.multiCountKey);

            if (multiCountAnswered) {
                answeredQuestionKeys.add(runtimeNode.multiCountKey);
            }
        }

        const extra = runtimeNode.multiCountKey ? 1 : 0;
        const firstUnansweredKey = !multiCountAnswered
            ? runtimeNode.multiCountKey
            : traversal.haltedKey;

        nodes.set(nodeId, {
            nodeId,
            parentId: runtimeNode.parentId,
            childIds: runtimeNode.childIds,
            name: runtimeNode.name,
            displayName: runtimeNode.displayName,
            sectionPath: runtimeNode.sectionPath,
            level: runtimeNode.level,
            reportSectionPath: runtimeNode.reportSectionPath,
            reportLevel: runtimeNode.reportLevel,
            inReport: runtimeNode.inReport,
            isMultiContainer: runtimeNode.isMultiContainer,
            isMultiInstance: runtimeNode.isMultiInstance,
            multiIndex: runtimeNode.multiIndex,
            multiCount: runtimeNode.multiCount,
            multiCountKey: runtimeNode.multiCountKey,
            multiCountAnswered,
            questionKeys: traversal.questionKeys,
            questionIds: traversal.questionIds,
            visibleCount: ownVisible + extra,
            answeredCount: ownAnswered + (multiCountAnswered ? extra : 0),
            firstUnansweredQuestionKey: firstUnansweredKey,
            firstUnansweredQuestionId: !multiCountAnswered ? null : traversal.haltedQuestionId,
            allAnswered: traversal.haltedKey === null && multiCountAnswered,
            allAnsweredDeep: false,
            chosen: traversal.chosen,
        });
    }

    for (let index = tree.order.length - 1; index >= 0; index -= 1) {
        const nodeId = tree.order[index] as NodeId;
        const visibility = nodes.get(nodeId) as NodeVisibility;
        let deep = visibility.allAnswered;

        if (deep) {
            for (const childId of visibility.childIds) {
                if (!(nodes.get(childId) as NodeVisibility).allAnsweredDeep) {
                    deep = false;
                    break;
                }
            }
        }

        (visibility as { allAnsweredDeep: boolean }).allAnsweredDeep = deep;
    }

    let firstIncompleteNodeId: NodeId | null = null;

    for (const nodeId of tree.order) {
        if (!(nodes.get(nodeId) as NodeVisibility).allAnswered) {
            firstIncompleteNodeId = nodeId;
            break;
        }
    }

    return {
        nodes,
        order: tree.order,
        visibleQuestionKeys,
        questionOwners,
        answeredQuestionKeys,
        firstIncompleteNodeId,
    };
}

/**
 * De vragen die in een onderdeel vóór dit onderdeel al gesteld worden. Die horen
 * daar: "Geen gebreken hier" vult ze hier niet in.
 */
export function questionsAskedBefore(
    visibility: VisibilityResult,
    nodeId: NodeId,
): ReadonlySet<AnswerKey> {
    const asked = new Set<AnswerKey>();
    const earlier = new Set<NodeId>();

    for (const candidate of visibility.order) {
        if (candidate === nodeId) {
            break;
        }

        earlier.add(candidate);
    }

    for (const [key, owner] of visibility.questionOwners) {
        if (earlier.has(owner)) {
            asked.add(key);
        }
    }

    return asked;
}

function isMultiCountAnswered(answers: AnswerSet, key: AnswerKey): boolean {
    const value = answers[key]?.value_number;

    /*
     * Leeg is nul, en nul is hier een antwoord: "er zijn er geen". Het aantal
     * bepaalt nog steeds hoeveel exemplaren er in de boom komen — nul dus — maar
     * het staat niet meer als onbeantwoorde vraag in de teller. Zie
     * {@link answeredQuestionKeys} voor dezelfde regel bij een gewone
     * aantalsvraag.
     */
    return value === null || value === undefined || Number.isFinite(value);
}
